import React, { useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    useGetNotificationsQuery,
    useMarkAsReadMutation,
    useMarkAllAsReadMutation,
    useDeleteNotificationMutation,
} from "../api";
import { useNotification } from "../hooks/useNotification";

interface NotificationsDropdownProps {
    isOpen: boolean;
    onClose: () => void;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation("notifications");
    const { showSuccess, showError } = useNotification();
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Получение данных
    const {
        data: notificationsData,
        isLoading,
        error,
    } = useGetNotificationsQuery({ page: 1, limit: 20 });

    // Мутации
    const [markAsRead] = useMarkAsReadMutation();
    const [markAllAsRead] = useMarkAllAsReadMutation();
    const [deleteNotification] = useDeleteNotificationMutation();

    // Закрытие при клике вне компонента
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Форматирование времени
    const formatTime = (timeString: string) => {
        const date = new Date(timeString);
        const now = new Date();
        const diffInMinutes = Math.floor(
            (now.getTime() - date.getTime()) / (1000 * 60)
        );

        if (diffInMinutes < 1) {
            return t("just_now");
        } else if (diffInMinutes < 60) {
            return t("minutes_ago", { count: diffInMinutes });
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60);
            return t("hours_ago", { count: hours });
        } else {
            const days = Math.floor(diffInMinutes / 1440);
            return t("days_ago", { count: days });
        }
    };

    // Обработчики действий
    const handleMarkAsRead = async (id: number, event?: React.MouseEvent) => {
        event?.preventDefault();
        event?.stopPropagation();
        try {
            await markAsRead(id).unwrap();
            showSuccess(t("marked_as_read"));
        } catch (error) {
            showError((error as any).message);
        }
    };

    const handleMarkAllAsRead = async (event?: React.MouseEvent) => {
        event?.preventDefault();
        event?.stopPropagation();
        try {
            await markAllAsRead().unwrap();
            showSuccess(t("all_marked_as_read"));
        } catch (error) {
            showError((error as any).message);
        }
    };

    const handleDelete = async (id: number, event?: React.MouseEvent) => {
        event?.preventDefault();
        event?.stopPropagation();
        try {
            await deleteNotification(id).unwrap();
            showSuccess(t("notification_deleted"));
        } catch (error) {
            showError((error as any).message);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
        >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-200">
                        {t("notifications")}
                    </h3>
                    {notificationsData?.data.some((n) => n.status === 0) && (
                        <button
                            onClick={(e) => handleMarkAllAsRead(e)}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                        >
                            {t("mark_all_read")}
                        </button>
                    )}
                </div>
            </div>

            <div className="max-h-96 overflow-y-auto">
                {isLoading ? (
                    <div className="p-4 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                            {t("loading")}
                        </p>
                    </div>
                ) : error ? (
                    <div className="p-4 text-center text-red-600 dark:text-red-400">
                        {t("error_loading")}
                    </div>
                ) : notificationsData?.data.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-300">
                        {t("no_notifications")}
                    </div>
                ) : (
                    <div className="divide-y divide-gray-200">
                        {notificationsData?.data.map((notification) => (
                            <div
                                key={notification.id}
                                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                                    notification.status === 0
                                        ? "bg-blue-50"
                                        : ""
                                }`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-900 dark:text-gray-200 leading-relaxed">
                                            {notification.text}
                                        </p>
                                        <p className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                                            {formatTime(notification.time)}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2 ml-2">
                                        {notification.status === 0 && (
                                            <button
                                                onClick={(e) =>
                                                    handleMarkAsRead(
                                                        notification.id,
                                                        e
                                                    )
                                                }
                                                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                                                title={t("mark_as_read")}
                                            >
                                                {t("read")}
                                            </button>
                                        )}
                                        <button
                                            onClick={(e) =>
                                                handleDelete(notification.id, e)
                                            }
                                            className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-600"
                                            title={t("delete")}
                                        >
                                            {t("delete")}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {notificationsData?.pagination &&
                notificationsData.pagination.last_page > 1 && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="text-xs text-gray-500 dark:text-gray-300 text-center">
                            {t("showing")} {notificationsData.data.length}{" "}
                            {t("of")} {notificationsData.pagination.total}{" "}
                            {t("notifications")}
                        </div>
                    </div>
                )}
        </div>
    );
};

export default NotificationsDropdown;
